import {
  Children,
  forwardRef,
  type ReactElement,
  type ReactNode,
  isValidElement,
  useMemo,
  useEffect,
} from 'react';
import { useIntl } from 'react-intl';
import { isElement } from 'react-is';

import {
  Button,
  type ButtonOwnProps,
} from '@dynatrace/strato-components/buttons';
import { DotMenuIcon } from '@dynatrace/strato-icons';

import { IntentButtonWithoutResponse } from './IntentButtonWithoutResponse.js';
import { IntentButtonWithResponse } from './IntentButtonWithResponse.js';
import { OpenWith } from './OpenWith.js';
import { createSdkLogger } from '../../core/sdk/sdk-logger.js';
import {
  Intent as Item,
  type IntentProps,
} from '../../core/slots/Intent/Intent.js';
import { isSlot } from '../../core/utils/is-slot.js';
import { InputGroup } from '../../layouts/input-group/InputGroup.js';
import { type MenuIntentProps } from '../../navigation/menu/Intent.js';
import { ItemIcon as Icon } from '../../navigation/menu/ItemIcon.js';
import { Menu } from '../../navigation/menu/Menu.js';

const SDK_LOGGER_NAME = 'Strato: IntentButton';

//#region Typings

/**
 * Accepted properties for the IntentButton.
 * @public
 */
export type IntentButtonProps = Exclude<IntentProps, 'onSelect' | 'icon'> &
  Pick<
    ButtonOwnProps,
    | 'onClick'
    | 'color'
    | 'variant'
    | 'width'
    | 'aria-disabled'
    | 'textAlign'
    | 'size'
  > & {
    disabled?: boolean;
  };

type IntentButtonSlots = {
  freeform: ReactNode[];
  items: ReactElement<IntentProps>[];
  icon?: ReactElement;
  openWith?: ReactElement<
    Omit<MenuIntentProps, 'onResponse' | 'options' | 'children'>
  >;
};
//#endregion

const getIntentButtonSlots = (children: ReactNode) => {
  const intentButtonSlots: IntentButtonSlots = {
    items: [],
    freeform: [],
  };

  Children.forEach(children, (child) => {
    if (isSlot(child, Item)) {
      intentButtonSlots.items.push(child);
    } else if (isSlot(child, OpenWith)) {
      intentButtonSlots.openWith = child;
    } else if (isSlot(child, Icon) && isElement(child.props.children)) {
      intentButtonSlots.icon = child.props.children;
    } else if (
      (child !== null && child !== undefined) ||
      isValidElement(child)
    ) {
      intentButtonSlots.freeform.push(child);
    }
  });

  return intentButtonSlots;
};

const _IntentButton = /* @__PURE__ */ forwardRef<
  HTMLDivElement,
  IntentButtonProps
>((props, forwardedRef) => {
  const {
    payload,
    children,
    onResponse,
    options,
    disabled,
    color,
    variant = 'emphasized',
    width,
    onClick,
    size,
    textAlign,
    'data-testid': dataTestId,
    'data-dtrum-mask': dataDtrumMask,
    'data-dtrum-allow': dataDtrumAllow,
    'aria-disabled': ariaDisabledProp,
    className: consumerClassName,
    style: consumerStyle,
    ...remainingProps
  }: IntentButtonProps = props;

  const logger = useMemo(() => createSdkLogger(SDK_LOGGER_NAME), []);

  useEffect(() => {
    if (!options.recommendedAppId || !options.recommendedIntentId) {
      logger.error(
        `Missing required intent options: recommendedAppId or recommendedIntentId. AppId: ${options.recommendedAppId}, IntentId: ${options.recommendedIntentId}`,
      );
    }
  }, [logger, options.recommendedAppId, options.recommendedIntentId]);

  const slots = getIntentButtonSlots(children);

  const intl = useIntl();
  const menuAriaLabel = intl.formatMessage({
    defaultMessage: 'Open intent menu',
    id: 'OEhnk3JokTy0c/JX',
    description: 'Aria label for the IntentButton menu trigger.',
  });

  const sharedProps: ButtonOwnProps = {
    color,
    variant,
    width,
    size,
    textAlign,
    onClick,
    disabled,
    'aria-disabled': ariaDisabledProp,
  };

  return (
    <InputGroup
      ref={forwardedRef}
      data-testid={dataTestId}
      data-dtrum-mask={dataDtrumMask}
      data-dtrum-allow={dataDtrumAllow}
      className={consumerClassName}
      style={consumerStyle}
      {...remainingProps}
    >
      {'responseProperties' in options ? (
        <IntentButtonWithResponse
          {...sharedProps}
          payload={payload}
          options={options}
          onResponse={onResponse ?? (() => undefined)}
          icon={slots.icon}
        >
          {slots.freeform}
        </IntentButtonWithResponse>
      ) : (
        <IntentButtonWithoutResponse
          {...sharedProps}
          payload={payload}
          options={options}
          icon={slots.icon}
        >
          {slots.freeform}
        </IntentButtonWithoutResponse>
      )}
      <Menu>
        <Menu.Trigger aria-label={menuAriaLabel}>
          <Button
            disabled={disabled}
            aria-disabled={ariaDisabledProp}
            variant={variant}
            color={color}
            size={size}
            style={{ width: 'max-content', minWidth: 'fit-content' }}
          >
            <DotMenuIcon />
          </Button>
        </Menu.Trigger>
        <Menu.Content
          alignment="end"
          data-dtrum-mask={dataDtrumMask}
          data-dtrum-allow={dataDtrumAllow}
        >
          {slots.items && (
            <Menu.Group>
              {slots.items.map((item, index) => (
                <Menu.Intent
                  {...item.props}
                  key={`${item.props.options.recommendedAppId}-${index}`}
                />
              ))}
            </Menu.Group>
          )}
          <Menu.Group>
            {slots.openWith ?? <OpenWith payload={payload} />}
          </Menu.Group>
        </Menu.Content>
      </Menu>
    </InputGroup>
  );
});

(_IntentButton as typeof _IntentButton & { displayName: string }).displayName =
  'IntentButton';

/**
 * The `IntentButton` is a dedicated component for sending
 * {@link https://developer.dynatrace.com/develop/intents/ | intents}.
 * The `IntentButton` only works within the AppShell context.
 * @public
 */
export const IntentButton = Object.assign(_IntentButton, {
  Item,
  OpenWith,
  Icon,
});
